//----------------------------------------------------------------------------------------------------------------------
// Outline Manager
//
// Manages named SelectionOutlineLayer instances for entity highlighting.
// Each layer tracks a Set<GameEntity> and rebuilds its selection on any change.
//----------------------------------------------------------------------------------------------------------------------

import { Color3, type Scene, SelectionOutlineLayer } from '@babylonjs/core';

import type { GameEntity } from '../classes/entity.ts';
import type { OutlineLayerConfig } from '../interfaces/level.ts';

//----------------------------------------------------------------------------------------------------------------------

interface OutlineLayerEntry
{
    layer : SelectionOutlineLayer;
    entities : Set<GameEntity>;
}

//----------------------------------------------------------------------------------------------------------------------

export class OutlineManager
{
    private _scene : Scene;
    private _layers = new Map<string, OutlineLayerEntry>();

    constructor(scene : Scene, configs ?: Record<string, OutlineLayerConfig>)
    {
        this._scene = scene;

        if(configs)
        {
            for(const [ name, config ] of Object.entries(configs))
            {
                this.create(name, config);
            }
        }
    }

    //------------------------------------------------------------------------------------------------------------------
    // Public API
    //------------------------------------------------------------------------------------------------------------------

    get(name : string) : SelectionOutlineLayer | undefined
    {
        return this._layers.get(name)?.layer;
    }

    create(name : string, config ?: OutlineLayerConfig) : SelectionOutlineLayer
    {
        const existing = this._layers.get(name);
        if(existing)
        {
            return existing.layer;
        }

        const layer = new SelectionOutlineLayer(`sage-outline-${ name }`, this._scene);

        if(config?.color)
        {
            layer.outlineColor = new Color3(config.color.r, config.color.g, config.color.b);
        }

        if(config?.thickness !== undefined) { layer.outlineThickness = config.thickness; }
        if(config?.occlusionStrength !== undefined) { layer.occlusionStrength = config.occlusionStrength; }

        this._layers.set(name, { layer, entities: new Set() });
        return layer;
    }

    highlightEntity(layerName : string, entity : GameEntity) : void
    {
        const entry = this._layers.get(layerName);
        if(!entry)
        {
            return;
        }

        entry.entities.add(entity);
        this._rebuildSelection(entry);
    }

    unhighlightEntity(layerName : string, entity : GameEntity) : void
    {
        const entry = this._layers.get(layerName);
        if(!entry)
        {
            return;
        }

        entry.entities.delete(entity);
        this._rebuildSelection(entry);
    }

    unhighlightEntityAll(entity : GameEntity) : void
    {
        for(const entry of this._layers.values())
        {
            if(entry.entities.delete(entity))
            {
                this._rebuildSelection(entry);
            }
        }
    }

    clear(layerName : string) : void
    {
        const entry = this._layers.get(layerName);
        if(!entry)
        {
            return;
        }

        entry.entities.clear();
        entry.layer.clearSelection();
    }

    dispose() : void
    {
        for(const entry of this._layers.values())
        {
            entry.entities.clear();
            entry.layer.dispose();
        }

        this._layers.clear();
    }

    //------------------------------------------------------------------------------------------------------------------
    // Private
    //------------------------------------------------------------------------------------------------------------------

    private _rebuildSelection(entry : OutlineLayerEntry) : void
    {
        entry.layer.clearSelection();

        for(const entity of entry.entities)
        {
            if(entity.node)
            {
                const meshes = entity.node.getChildMeshes();
                if(meshes.length > 0)
                {
                    entry.layer.addSelection(meshes);
                }
            }
        }
    }
}

//----------------------------------------------------------------------------------------------------------------------
